import React, { useMemo } from 'react';
import { Paper, Typography, Stack, Button, Box } from '@mui/material';
import Chart from 'react-apexcharts';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { exportCsvWithDate } from './csvUtil';

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const CHART_HEIGHT = 240;

export default function MissingReport({ missingRows = [], missingColumns = [], eventHasAdjustments, eventsMap = {}, teams = {} }) {
  const totalCount = (missingRows || []).length;

  // debug summary to help diagnose why only some teams appear
  const teamsCount = Object.keys(teams || {}).length;
  const eventsCount = Object.keys(eventsMap || {}).length;
  const uniqueMissingTeamUids = Array.from(new Set((missingRows || []).map(r => r.teamUid).filter(Boolean)));
  const sampleMissingTeams = uniqueMissingTeamUids.slice(0,10).map(uid => (teams[uid] && teams[uid].teamName) || uid);

  const countsByEvent = useMemo(() => {
    const map = {};
    (missingRows || []).forEach(r => {
      // try to use eventUid if available, otherwise parse from id
      const eventUid = r.eventUid || (r.id ? r.id.split('-')[0] : 'unknown');
      const name = r.eventName || (eventsMap[eventUid] && eventsMap[eventUid].name) || eventUid;
      if (!map[eventUid]) map[eventUid] = { name, count: 0 };
      map[eventUid].count++;
    });
    const arr = Object.keys(map).map(k => ({ eventUid: k, eventName: map[k].name, count: map[k].count }));
    arr.sort((a,b) => b.count - a.count);
    return arr.slice(0, 10);
  }, [missingRows, eventsMap]);

  const chartOptions = useMemo(() => ({
    chart: { id: 'missing-top10', toolbar: { show: true }, animations: { enabled: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: '60%' } },
    xaxis: { categories: countsByEvent.map(c => c.eventName), labels: { rotate: -45, hideOverlappingLabels: true } },
    yaxis: { title: { text: 'Count' } },
    dataLabels: { enabled: false },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: false,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const labels = (w.config.xaxis && w.config.xaxis.categories) || [];
        const label = labels[dataPointIndex] || '';
        const val = series[seriesIndex] && series[seriesIndex][dataPointIndex] !== undefined ? series[seriesIndex][dataPointIndex] : 0;
        return `<div style="pointer-events:none;padding:8px;font-size:13px;"><strong>${escapeHtml(label)}</strong><div style="margin-top:6px">Count: <strong>${val}</strong></div></div>`;
      }
    }
  }), [countsByEvent]);

  const chartSeries = [{ name: 'Missing Scores', data: countsByEvent.map(c => c.count) }];

  const handleExportRows = () => {
    const headers = missingColumns.map(c => c.field).concat(['eventUid','scoreUid']);
    const rows = (missingRows || []).map(r => {
      const base = {};
      missingColumns.forEach(c => { base[c.field] = r[c.field]; });
      base.eventUid = r.eventUid || (r.id ? r.id.split('-')[0] : '');
      base.scoreUid = r.scoreUid || '';
      return base;
    });
    exportCsvWithDate('missing-scores', headers, rows);
  };

  const handleExportHistogram = () => {
    const headers = ['eventUid','eventName','count'];
    const rows = countsByEvent.map(c => ({ eventUid: c.eventUid, eventName: c.eventName, count: c.count }));
    exportCsvWithDate('missing-scores-top10', headers, rows);
  };

  const columns = useMemo(() => {
    const base = (missingColumns || []).slice();
    base.push({ field: 'enter', headerName: 'Enter Score', width: 180, sortable: false, filterable: false, renderCell: (params) => {
      const r = params.row || {};
      const eventUid = r.eventUid || (r.id ? r.id.split('-')[0] : '');
      const teamUid = r.teamUid || '';
      const href = `/scoring/new/for-event/${eventUid}/for-team/${teamUid}`;
      return (
        <Button size="small" variant="outlined" onClick={() => window.location.replace(href)}>
          Enter Score
        </Button>
      );
    }});
    return base;
  }, [missingColumns]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">Missing Scores</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={handleExportHistogram}>Export Top 10</Button>
            <Button size="small" variant="contained" onClick={handleExportRows}>Export Rows</Button>
          </Stack>
        </Stack>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Total missing scores: {totalCount}</Typography>
            <Typography variant="body2">These rows represent teams that have no submitted score for the event.</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {`Debug: teams=${teamsCount}, events=${eventsCount}, uniqueMissingTeams=${uniqueMissingTeamUids.length}${uniqueMissingTeamUids.length > 0 ? ' — sample: ' + sampleMissingTeams.join(', ') : ''}`}
            </Typography>
          </Box>
          <Box sx={{ width: 600 }}>
            <Chart type="bar" series={chartSeries} options={chartOptions} height={CHART_HEIGHT} />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 1 }}>
        <div style={{ height: 520, width: '100%' }}>
          <DataGrid
            rows={missingRows}
            columns={columns}
            slots={{ toolbar: GridToolbar }}
            pageSizeOptions={[25,50,100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </div>
      </Paper>
    </Box>
  );
}
